import { generateUUID } from '@frontend/duck-tape';
import { useAdvertisingHook } from '@frontend/react/hooks/getUseAdvertisingHook';
import { getUseAnalyticsHook } from '@frontend/react/hooks/getUseAnalyticsHook/getUseAnalyticsHook';
import { useSegment } from '@frontend/web-react/hooks/useSegment';
import { getSegment } from '@frontend/web-utils/segment';
import { addSentryBreadcrumb } from '@frontend/web-utils/sentry';
import { useRetrieveClientQuery } from '@frontend/web/hooks/authApi';
import { Env } from '@frontend/web/utils/env';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const generateConversionId = (): UUID => generateUUID();

export const useAnalytics = () => {
  const segment = getSegment();
  const { track: segmentTrack, trackRouteChange } = useSegment({ analytics: segment, env: Env.VITE_ENV });
  const { data: client } = useRetrieveClientQuery();

  const { track: originalTrack } = useMemo(() => getUseAnalyticsHook({ client, segmentTrack }), [client, segmentTrack]);
  const { trackAdvertising } = useAdvertisingHook();
  const originalTrackRef = useRef(originalTrack);

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    originalTrackRef.current = originalTrack;
  }, [originalTrack]);

  const track = useCallback<typeof originalTrack>((config) => {
    addSentryBreadcrumb({
      category: 'analytics',
      data: config,
      level: 'info',
      message: `Analytics: ${config.topic}.${config.event.toString()}`,
      type: 'info',
    });

    originalTrackRef.current(config);
  }, []);

  return { generateConversionId, track, trackAdvertising, trackRouteChange };
};
